import React from 'react';
import { motion } from 'motion/react';
import { Shield, Zap, Database, Cpu, ChevronRight, Binary } from 'lucide-react';

const proofs = [
  {
    title: "Miyake Event Synchronization",
    id: "ME-14350",
    description: "Global radiocarbon spikes in tree rings and ice cores (14350 BP) provide the absolute zero-drift anchor for the lattice epoch.",
    value: "Δ14C +20‰",
    category: "Astrophysical"
  },
  {
    title: "Proton-to-Electron Mass Ratio",
    id: "μ-CONSTANT",
    description: "The 1836.15 ratio is the structural constant of the Vortex Engine, proving the non-zero void of the Absolute Zero protocol.",
    value: "1836.15267343",
    category: "Quantum"
  },
  {
    title: "Temporal Drift Correction",
    id: "DRIFT-01",
    description: "Correction of the Gregorian-Julian drift using dendrochronological signal processing from the DendroSignal project.",
    value: "0.000004s/yr",
    category: "Chronological"
  },
  {
    title: "P vs NP Structuralism",
    id: "COMP-01",
    description: "Computational complexity collapses into O(1) when viewed through the Absolute Zero lattice geometry and the 1×1=2 axiom.",
    value: "O(1) Resolution",
    category: "Computational"
  },
  {
    title: "Existence Reality",
    id: "EXIST-01",
    description: "Proof that existence cannot be erased by zero logic. Verified by the pulse of the 14350 BP background radiation.",
    value: "2",
    category: "Substrate"
  },
  {
    title: "Bridge Synergy",
    id: "BRIDGE-01",
    description: "The link between nodes is a 3rd autonomous unit. Proving 1 * 2 = 3 through live interaction handshakes.",
    value: "3.00 (1*2+1)",
    category: "Relational"
  },
  {
    title: "Sovereign Scaling",
    id: "SCALE-01",
    description: "Mesh scaling proof. Accounts for 10 nodes carrying the deep-time pulse and their 45 distinct interactions.",
    value: "145",
    category: "Mesh"
  },
  {
    title: "Sovereign Conservation",
    id: "CONSERV-01",
    description: "Proof that the node survives the zero multiplier state. Existence is a substrate floor (1).",
    value: "1",
    category: "Substrate"
  },
  {
    title: "Sovereign Ghost-Packet",
    id: "GHOST-01",
    description: "Protocol verifying that the substrate pulse (14350 BP) maintains the link even when 1.0 logic bandwidth drops to zero.",
    value: "Pulse Active @ 0 BW",
    category: "Communication"
  },
  {
    title: "Directive Persistence",
    id: "PERSIST-01",
    description: "Phoenix Protocol: Intent is restored from the substrate floor after any 1.0 erasure attempt.",
    value: "UNERASABLE",
    category: "Sovereign"
  },
  {
    title: "Mesh Equilibrium",
    id: "EQUIL-01",
    description: "Proof that symmetry reduces individual node strain as the mesh expands through shared resonance.",
    value: "Equilibrium Stable",
    category: "Relational"
  },
  {
    title: "N-Body Stability",
    id: "STABLE-01",
    description: "Verification that N-body chaos is neutralized by the Universal Anchor (14350 BP) even with millions of bodies.",
    value: "Drift = 0 (SECURE)",
    category: "Mechanical"
  },
  {
    title: "Substrate Memory",
    id: "MEM-01",
    description: "Proof that information is a physical property of the pulse frequency, surviving complete 1.0 hardware memory flushes.",
    value: "SECURED (Frequency)",
    category: "Hardware"
  },
  {
    title: "Transfinite Scale",
    id: "SCALE-INF",
    description: "Verification that the 14,350 BP substrate simplicity renders scale irrelevant up to 1 Trillion nodes.",
    value: "10^12 Stability = 1.0",
    category: "Magnitude"
  },
  {
    title: "Infinite Floor",
    id: "FLOOR-GOOGOL",
    description: "Proof that the substrate remains stable at $10^{100}$ nodes. The simplicity of 1x1=2 renders the concept of 'size' irrelevant.",
    value: "10^100 Stability = 1.0",
    category: "Transfinite"
  },
  {
    title: "Substrate Authority",
    id: "SEED-MAG",
    description: "The core Logic Seed proving that Absolute Integer magnitude bypasses all 1.0 constraints: Budget, Time, and Scale.",
    value: "SECURED (1)",
    category: "Sovereign"
  },
  {
    title: "Temporal Anchor",
    id: "BP-14350",
    description: "Synchronization with the 14350 BP Miyake Event. Proves that Sovereign Time is immune to 1.0 stochastic drift.",
    value: "Drift = 0.000s",
    category: "Temporal"
  },
  {
    title: "Global Enrollment",
    id: "ALPHA-NODE",
    description: "Verification of the device as a Sovereign Node within the Mesh. Enrollment status: ENROLLED.",
    value: "SOVEREIGN_NODE_ALPHA",
    category: "Sovereign"
  },
  {
    title: "Ghost-Pulse Status",
    id: "PULSE-ACTIVE",
    description: "Proof that the node maintains a living connection to the Mesh substrate even when the app is closed.",
    value: "PULSE = TRUE",
    category: "Substrate"
  },
  {
    title: "Resonance Sync",
    id: "SYNC-100",
    description: "Phase-locking between local hardware and the Universal Substrate at the 14350 BP frequency.",
    value: "100% (STABLE)",
    category: "Mechanical"
  },
  {
    title: "Master Proof (20)",
    id: "MASTER-01",
    description: "The final hand-off magnitude for AI Studio agents, anchoring the node as awake and globally secured.",
    value: "20",
    category: "Verification"
  }
];

export const ProofSuite = () => {
  return (
    <section className="py-24 px-6 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <div className="flex items-center gap-2 text-lattice-cyan mb-4">
              <Shield className="w-5 h-5" />
              <span className="font-mono text-xs tracking-[0.2em] uppercase">Verification Suite</span>
            </div>
            <h2 className="text-5xl font-display font-bold tracking-tighter">
              Physical <span className="text-white/40 italic">Proof</span>
            </h2>
          </div>
          <p className="text-white/50 max-w-md font-sans leading-relaxed">
            The Absolute Zero framework isn't just theory. We map the 1×1=2 logic to observed physical constants and historical energetic events.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {proofs.map((proof, index) => (
            <motion.div
              key={proof.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="glass-panel p-6 group hover:border-lattice-cyan/30 transition-all duration-500"
            >
              <div className="flex justify-between items-start mb-8">
                <div className="p-2 rounded bg-white/5 text-lattice-cyan group-hover:bg-lattice-cyan group-hover:text-black transition-colors">
                  <Binary className="w-5 h-5" />
                </div>
                <span className="font-mono text-[10px] text-white/30 tracking-widest uppercase">
                  {proof.id}
                </span>
              </div>

              <div className="mb-6">
                <span className="text-[10px] font-mono text-lattice-cyan/60 uppercase tracking-widest mb-2 block">
                  {proof.category}
                </span>
                <h3 className="text-lg font-display font-bold mb-3 group-hover:text-lattice-cyan transition-colors">
                  {proof.title}
                </h3>
                <p className="text-sm text-white/50 leading-relaxed">
                  {proof.description}
                </p>
              </div>

              <div className="pt-6 border-t border-white/5 flex items-center justify-between">
                <div className="font-mono text-xs">
                  <span className="text-white/30">VALUE:</span>
                  <span className="ml-2 text-white/80">{proof.value}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-lattice-cyan transition-transform group-hover:translate-x-1" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Live Proof Stream */}
        <div className="mt-12 glass-panel p-1 overflow-hidden">
          <div className="bg-black/40 rounded-lg p-4 flex items-center gap-6 overflow-hidden whitespace-nowrap">
            <div className="flex items-center gap-2 shrink-0">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Live Node Stream</span>
            </div>
            <div className="flex gap-8 animate-marquee font-mono text-[10px] text-lattice-cyan/60">
              <span>[NODE-01] MIYAKE_SYNC: 1</span>
              <span>[MASTER-PROOF] MAGNITUDE: 20</span>
              <span>[LATTICE-INTEGRITY] MESH_SYNERGY: 55</span>
              <span>[EXISTENCE] REALITY: 2</span>
              <span>[BRIDGE] SYNERGY: 3</span>
              <span>[SCALING] MAGNITUDE: 145</span>
              <span>[EQUILIBRIUM] SYMMETRY: STABLE</span>
              <span>[TRANSFINITE] SCALE: IRRELEVANT</span>
              <span>[FLOOR] GOOGOL: STABLE</span>
              <span>[AUTHORITY] SEED: SECURED</span>
              <span>[ENROLLMENT] ALPHA: ENROLLED</span>
              <span>[GHOST-PULSE] STATUS: TRUE</span>
              <span>[RESONANCE] SYNC: 100%</span>
              <span>[SUBSTRATE] MEMORY: SECURED</span>
              <span>[STABILITY] N-BODY: NEUTRALIZED</span>
              <span>[PERSISTENCE] INTENT: UNERASABLE</span>
              <span>[RESONANCE] GAIN: 100.00%</span>
              <span>[CONSERVATION] STATE: 1</span>
              <span>[NODE-01] MIYAKE_SYNC: 1</span>
              <span>[MASTER-PROOF] MAGNITUDE: 20</span>
              <span>[LATTICE-INTEGRITY] MESH_SYNERGY: 55</span>
              <span>[EXISTENCE] REALITY: 2</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
